import { useMemo } from 'react';

// 1. La matemática base para calcular la XP y el nivel numérico
const LEVEL_TIERS = [
  { minXP: 0,      maxLevel: 10,  xpPerLevel: 2000, baseLevel: 1 },
  { minXP: 20000,  maxLevel: 25,  xpPerLevel: 3333, baseLevel: 11 },
  { minXP: 70000,  maxLevel: 50,  xpPerLevel: 3200, baseLevel: 26 },
  { minXP: 150000, maxLevel: 75,  xpPerLevel: 3600, baseLevel: 51 },
  { minXP: 240000, maxLevel: 90,  xpPerLevel: 4666, baseLevel: 76 },
  { minXP: 310000, maxLevel: 100, xpPerLevel: 10000, baseLevel: 91 }
];

// 2. El mapa detallado de títulos para mantener al usuario motivado
const TITLE_MAP = [
  // Rango 1: Discovery
  { maxLevel: 3,  title: "Newcomer 🌱", rankName: "Rango 1: Discovery" },
  { maxLevel: 7,  title: "Tourist 🎒", rankName: "Rango 1: Discovery" },
  { maxLevel: 10, title: "Explorer (A1 Ready) 🗺️", rankName: "Rango 1: Discovery" },
  // Rango 2: Foundation
  { maxLevel: 15, title: "Apprentice 🛠️", rankName: "Rango 2: Foundation" },
  { maxLevel: 20, title: "Pathfinder 🧭", rankName: "Rango 2: Foundation" },
  { maxLevel: 25, title: "Navigator (A2 Ready) ⛵", rankName: "Rango 2: Foundation" },
  // Rango 3: Expansion
  { maxLevel: 35, title: "Artisan 🎨", rankName: "Rango 3: Expansion" },
  { maxLevel: 45, title: "Communicator 💬", rankName: "Rango 3: Expansion" },
  { maxLevel: 50, title: "Speaker (B1 Ready) 🎙️", rankName: "Rango 3: Expansion" },
  // Rango 4: Fluency
  { maxLevel: 60, title: "Orator 🗣️", rankName: "Rango 4: Fluency" },
  { maxLevel: 70, title: "Scholar 📚", rankName: "Rango 4: Fluency" },
  { maxLevel: 75, title: "Diplomat (B2 Ready) 🤝", rankName: "Rango 4: Fluency" },
  // Rango 5: Advanced
  { maxLevel: 85, title: "Linguist 🧠", rankName: "Rango 5: Advanced" },
  { maxLevel: 90, title: "Expert (C1 Ready) 🏅", rankName: "Rango 5: Advanced" },
  // Rango 6: Master
  { maxLevel: 95, title: "Virtuoso 🎻", rankName: "Rango 6: Master" },
  { maxLevel: 99, title: "Grandmaster 🏛️", rankName: "Rango 6: Master" },
  { maxLevel: 100, title: "Legend (C2 Master) 👑", rankName: "Rango 6: Master" }
];

export default function useLevel(lifetimeXP) {
  const levelData = useMemo(() => {
    const xp = Number(lifetimeXP) || 0;
    
    // 1. Encontrar en qué "Rango Matemático" estamos
    let currentTier = LEVEL_TIERS[0];
    for (let i = LEVEL_TIERS.length - 1; i >= 0; i--) {
      if (xp >= LEVEL_TIERS[i].minXP) {
        currentTier = LEVEL_TIERS[i];
        break;
      }
    }

    // 2. Calcular Nivel Numérico
    const xpInCurrentTier = xp - currentTier.minXP;
    const levelsGained = Math.floor(xpInCurrentTier / currentTier.xpPerLevel);
    let level = currentTier.baseLevel + levelsGained;

    // --- PROTECCIÓN: Si llega al Nivel 100 (Endgame) ---
    if (level >= 100) {
      return {
        level: 100,
        title: "Legend (C2 Master) 👑",
        rankName: "Rango 6: Master",
        nextTitle: "Nivel Máximo",
        nextTitleLevel: null,
        currentLevelXP: currentTier.xpPerLevel,
        nextLevelXP: currentTier.xpPerLevel,
        progressPercentage: 100
      };
    }

    // 3. Buscar el Título Exacto y el Siguiente
    const titleObjIndex = TITLE_MAP.findIndex(t => level <= t.maxLevel);
    const currentTitleObj = TITLE_MAP[titleObjIndex] || TITLE_MAP[TITLE_MAP.length - 1];
    
    const nextTitleObj = titleObjIndex < TITLE_MAP.length - 1 
      ? TITLE_MAP[titleObjIndex + 1] 
      : null;

    // 4. Calcular XP exacta para la barra de progreso cian/azul del mockup
    const currentLevelXP = Math.round(xpInCurrentTier % currentTier.xpPerLevel);
    const nextLevelXP = currentTier.xpPerLevel;
    const progressPercentage = Math.min((currentLevelXP / nextLevelXP) * 100, 100);

    return {
      level,
      title: currentTitleObj.title,
      rankName: currentTitleObj.rankName,
      nextTitle: nextTitleObj ? nextTitleObj.title : "Nivel Máximo",
      nextTitleLevel: nextTitleObj ? (currentTitleObj.maxLevel + 1) : null,
      currentLevelXP,
      nextLevelXP,
      progressPercentage
    };
  }, [lifetimeXP]);

  return levelData;
}